
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchEventCategories } from "@/services/eventService";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface EventCategoryFilterProps {
  activeCategory: string | undefined;
  setActiveCategory: (category: string | undefined) => void;
}

const EventCategoryFilter: React.FC<EventCategoryFilterProps> = ({ 
  activeCategory, 
  setActiveCategory 
}) => {
  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['eventCategories'],
    queryFn: fetchEventCategories,
    retry: 2,
    retryDelay: 1000,
  });

  // Always render at least the "All Events" button
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium mb-4">Event Categories</h3>
      <div className="flex flex-wrap gap-2">
        <Button
          key="all"
          variant={activeCategory === undefined ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveCategory(undefined)}
          className={activeCategory === undefined ? "bg-eco-500 hover:bg-eco-600" : ""}
        >
          All Events
        </Button>
        
        {isLoading ? (
          <>
            <Skeleton className="h-9 w-16" />
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-20" />
          </>
        ) : (
          categories.map((category) => (
            <Button
              key={category}
              variant={activeCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveCategory(category)}
              className={activeCategory === category ? "bg-eco-500 hover:bg-eco-600" : ""}
            >
              {category}
            </Button>
          ))
        )}
        
        {error && (
          <div className="text-sm text-red-500">
            Failed to load categories
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCategoryFilter;
